import type {
  Instructor_Instructor as Instructor,
  Partner_Partner as Partner,
} from '__generated__/graphql-types-do-not-use';

import path from 'js/lib/path';

import { COURSE_TYPES } from 'bundles/course-cards/constants';
import type { EnterpriseCourseCardCourseQueryQuery } from 'bundles/course-cards/queries/__generated__/EnterpriseCourseCardCourseQuery';
import type { ReplaceCustomContent } from 'bundles/custom-labels/types/CustomLabels';
import type { CourseStatus, CoursesV1 } from 'bundles/naptimejs/resources/__generated__/CoursesV1';

import _t from 'i18n!nls/course-cards';

export const getCourseTypeLabel = (
  courseType: COURSE_TYPES,
  shouldCourseBeLowerCased = false,
  replaceCustomContent: ReplaceCustomContent
) => {
  switch (courseType) {
    case COURSE_TYPES.STANDARD_COURSE:
      return shouldCourseBeLowerCased
        ? replaceCustomContent(_t('#{course}'), { returnsString: true })
        : replaceCustomContent(_t('#{capitalizedCourse}'), { returnsString: true });
    case COURSE_TYPES.PROJECT:
    case COURSE_TYPES.RHYME_PROJECT:
      return _t('Project');
    default:
      throw new Error(`The courseType ${courseType} is not supported.`);
  }
};

export const getConsumerCourseType = (courseType: string | undefined) => {
  switch (courseType) {
    case 'Course_ProjectMetadata':
      return COURSE_TYPES.PROJECT;
    case 'Course_RhymeProjectMetadata':
      return COURSE_TYPES.RHYME_PROJECT;
    case 'Course_ShortFormContentMetadata':
      return COURSE_TYPES.STANDARD_COURSE;
    case 'Course_StandardCourseMetadata':
      return COURSE_TYPES.STANDARD_COURSE;
    default:
      return COURSE_TYPES.STANDARD_COURSE;
  }
};

export type CourseDataType = NonNullable<EnterpriseCourseCardCourseQueryQuery['Course']>['queryById'];

/**
 * GraphQL to CoursesV1 mapping
 */
// Is course launched
export const isCourseLaunched = (courseData: CourseDataType): boolean => {
  const now = Date.now();
  return courseData?.launchedAt ? new Date(courseData.launchedAt).valueOf() < now : false;
};

// Is course pre-enrolling
export const isCoursePreEnrolling = (courseData: CourseDataType): boolean => {
  const now = Date.now();
  const isLaunched = isCourseLaunched(courseData);
  return courseData?.preEnrollmentEnabledAt
    ? !isLaunched && new Date(courseData.preEnrollmentEnabledAt).valueOf() < now
    : false;
};

// Get course status
export const getCourseStatus = (courseData: CourseDataType): string => {
  const isLaunched = isCourseLaunched(courseData);
  const isPreEnrolling = isCoursePreEnrolling(courseData);
  return isLaunched && courseData?.isReal ? 'launched' : isPreEnrolling && courseData?.isReal ? 'preenroll' : 'draft';
};

// Get course type
export const getCourseType = (courseData: CourseDataType): string => {
  const isCapstone = courseData?.specializations?.some((s12n: { capstoneCourses?: Array<{ id: string }> }) =>
    s12n.capstoneCourses?.some((capstone: { id: string }) => capstone.id === courseData.id)
  );
  return isCapstone ? 'v2.capstone' : 'v2.ondemand';
};

// Compute course URLs following the same logic as the Naptime resource
const computeCourseUrls = (slug: string, courseType: string | undefined) => {
  // All new courses are on-demand in the new world
  const isOnDemand = courseType === 'v2.ondemand';

  // Handle empty slug
  if (!slug) {
    return { link: '/', homeLink: '/' };
  }

  // Base link computation
  const link = path.join('/', isOnDemand ? 'learn' : 'course', slug);

  // Home link computation
  const homeLink = path.join('/', 'learn', slug, 'home', 'welcome');

  return { link, homeLink };
};

export const mapGraphQLToCourseV1 = (courseData: CourseDataType): Partial<CoursesV1> & { homeLink: string } => {
  const slug = courseData?.slug ?? '';
  const courseStatus = getCourseStatus(courseData);
  const courseType = getCourseType(courseData);
  const { homeLink } = computeCourseUrls(slug, courseType);

  return {
    id: courseData?.id ?? undefined,
    name: courseData?.name ?? undefined,
    slug,
    description: courseData?.textDescription ?? undefined,
    photoUrl: courseData?.promoPhotoUrl ?? undefined,
    upcomingSessionStartDate: courseData?.upcomingCourseSession?.startsAt
      ? new Date(courseData.upcomingCourseSession.startsAt).getTime()
      : undefined,
    workload: courseData?.estimatedWorkload ?? undefined,
    courseType,
    courseStatus: courseStatus as CourseStatus,
    plannedLaunchDate: courseData?.plannedLaunchDate ?? undefined,
    partnerIds: courseData?.partners?.map((partner: Pick<Partner, 'id'>) => partner.id) ?? undefined,
    instructorIds: courseData?.instructors?.map((instructor: Pick<Instructor, 'id'>) => instructor.id) ?? undefined,
    homeLink,
  };
};

export const getEnterpriseCourseHomeLinkLabel = (progressPercentage?: number | null) => {
  return progressPercentage === 0 ? _t('Get started') : _t('Resume');
};
